import { createAction } from '@reduxjs/toolkit'
import { call, fork, put, select, takeLatest } from 'redux-saga/effects'

import { getAndActivateFormByType } from '../effects/getFormByType'
import { handleCloseMaterialForm } from '../effects/handleCloseMaterialForm'
import { AISuggestion } from '../../../models/aiClassifications'
import { AzureFeatureFlag } from '../../../models/azureFeatureFlags'
import { AzureFeatureFlagIds } from '../../../shared/constants/azure-feature-flags'
import {
    AI_MATERIAL_REFLECTED_GRADIENT_COLORS,
    DRAWABLE_TYPES,
    SELECTED_AI_SUGGESTION_COLOR,
} from '../../../shared/constants/drawable-types'
import { FormTypes } from '../../../shared/constants/form'
import { isAzureFeatureFlagEnabled } from '../../../shared/services/azure-feature-flag-services/azure-feature-flag-services'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Color, Workspace } from '../../lib/toolBoxes/2D'
import {
    createDrawableLocation,
    deleteSelectedAiSuggestionId,
    getSelectedAIMaterial,
    selectAiSuggestions,
    selectedAiSuggestionIds,
    selectSelectedMaterialSubType,
    setActiveAiDrawableGroup,
    setSelectedAiSuggestionIds,
    updateSelectedAIMaterialType,
} from '../../slices/2D'
import { selectAzureFeatureFlags } from '../../slices/flags'

export const updateSelectedAISuggestion = createAction<{
    aiSuggestionIDs: string[]
    aiDrawableID?: number
    isGroupVisible?: boolean
}>('updateSelectedAISuggestion')

export function* handleToggleAIDrawables(
    selectedIDs: string[],
    itemSelectionStatus: boolean,
    selectedAIMaterialType: DRAWABLE_TYPES,
    selectedAiSuggestionIds: number
) {
    const manager: PaperManager = yield call(managers.get2DManager)

    if (!manager) return

    const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)

    const aiDrawables: paper.Path[] = yield call(workspaceTool.getItemsWithCriteria, 'data', function (data) {
        return selectedIDs.includes(data?.aiSuggestion?.id)
    })

    const selectedSubType: DRAWABLE_TYPES | null = yield select(selectSelectedMaterialSubType)

    for (const aiDrawable of aiDrawables) {
        const colorTool: Color = yield call(manager.getTool, Color.NAME)
        const selectedColor = yield call(colorTool.createColor, SELECTED_AI_SUGGESTION_COLOR)
        const colorTypeKey =
            (selectedAIMaterialType === DRAWABLE_TYPES.BEAM
                ? aiDrawable.data?.aiSuggestion?.settings.beam_type
                : selectedAIMaterialType) || 'default'
        const { outerColor, innerColor } = AI_MATERIAL_REFLECTED_GRADIENT_COLORS[colorTypeKey]
        const gradientColor = yield call(
            colorTool.createReflectedGradient,
            outerColor,
            innerColor,
            aiDrawable.data.aiSuggestion.coordinates
        )

        if (!itemSelectionStatus) {
            aiDrawable.strokeColor = selectedColor
            if (selectedAiSuggestionIds === 0) {
                yield call(workspaceTool.lockItems, true, undefined, selectedAIMaterialType, selectedSubType)
            }
        } else {
            aiDrawable.strokeColor = gradientColor
        }
    }
}

export function* handleUpdateSelectedAISuggestion(aiSuggestionPayload: {
    type: string
    payload: {
        aiSuggestionIDs: string[]
        aiDrawableID?: number
        isGroupVisible?: boolean
    }
}) {
    try {
        const selectedIDs = aiSuggestionPayload.payload.aiSuggestionIDs
        const azureFeatureFlags: AzureFeatureFlag[] | null = yield select(selectAzureFeatureFlags)

        const isAISuggestionsFilterFlagEnabled: boolean = yield call(
            isAzureFeatureFlagEnabled,
            azureFeatureFlags,
            AzureFeatureFlagIds.ai_suggestions_filter
        )

        if (isAISuggestionsFilterFlagEnabled) {
            const selectedAIMaterialType: DRAWABLE_TYPES = yield select(getSelectedAIMaterial)
            const aiSuggestions = yield select(selectAiSuggestions)
            const selectedSuggestionIds = yield select(selectedAiSuggestionIds)

            const itemSelectionStatus =
                selectedIDs.length === 1
                    ? selectedSuggestionIds.includes(selectedIDs[0])
                    : !aiSuggestionPayload.payload.isGroupVisible

            for (const id of selectedIDs) {
                if (!itemSelectionStatus) {
                    yield put(setSelectedAiSuggestionIds(id))

                    yield fork(getAndActivateFormByType, selectedAIMaterialType, FormTypes.materials)
                    const suggestion: AISuggestion = aiSuggestions.find((suggestion) => suggestion.id === id)

                    yield put(setActiveAiDrawableGroup(suggestion))
                } else {
                    yield put(deleteSelectedAiSuggestionId(id))
                }
            }

            if (itemSelectionStatus) {
                const areAllIdsUnselected =
                    selectedIDs.length === selectedSuggestionIds.length &&
                    selectedIDs
                        .slice()
                        .sort()
                        .every((val, index) => val === selectedSuggestionIds.slice().sort()[index])

                if (areAllIdsUnselected) {
                    yield put(updateSelectedAIMaterialType(null))
                    yield put(setActiveAiDrawableGroup(null))
                    yield fork(handleCloseMaterialForm)
                }
            }

            yield call(
                handleToggleAIDrawables,
                selectedIDs,
                itemSelectionStatus,
                selectedAIMaterialType,
                selectedSuggestionIds.length
            )
        } else if (aiSuggestionPayload.payload.aiDrawableID) {
            yield put(createDrawableLocation(aiSuggestionPayload.payload.aiDrawableID))
        }
    } catch (error) {
        yield call(console.error, error)
    }
}

export function* watchForUpdateSelectedAISuggestion() {
    yield takeLatest(updateSelectedAISuggestion.type, handleUpdateSelectedAISuggestion)
}
